/**
 * Hurdle extraction, visibility filtering, and tactical envelope precomputation.
 */

import { Coordinates } from '~/app';
import Turfmaster, { Field } from '~/server/src/tmmodel';

import { BoardHurdle } from './hurdleTypes';

/** Squares before hurdle centerline in game direction (opposite nf). */
export const HURDLE_POI_BEFORE_SQUARES = 1;
/** Squares behind hurdle in game direction (along nf). */
export const HURDLE_POI_BEHIND_SQUARES = 3;
/** Lateral lane padding on each side of hurdle lane. */
export const HURDLE_POI_LANE_PADDING = 1;

const GOAL_LINE_PROG = 120;

function fieldKey(lane: number, pos: number): string {
  return `${lane}:${pos}`;
}

function getFieldAt(lanes: Field[][], lane: number, pos: number): Field | undefined {
  if (lane < 0 || lane >= lanes.length) return undefined;
  return lanes[lane]?.[pos];
}

function buildPredecessorMap(lanes: Field[][]): Map<string, Field> {
  const pred = new Map<string, Field>();
  lanes.forEach((laneFields, lane) => {
    laneFields.forEach((field) => {
      if (field.nf >= 0) {
        const next = getFieldAt(lanes, lane, field.nf);
        if (next) pred.set(fieldKey(lane, next.pos), field);
      }
    });
  });
  return pred;
}

function addFieldToSet(
  lanes: Field[][],
  field: Field,
  seen: Set<string>,
  out: Coordinates[],
): void {
  const key = fieldKey(field.lane, field.pos);
  if (seen.has(key)) return;
  seen.add(key);
  out.push({ x: field.x, y: field.y });
}

function addLateralNeighbors(
  lanes: Field[][],
  field: Field,
  seen: Set<string>,
  out: Coordinates[],
): void {
  for (let d = -HURDLE_POI_LANE_PADDING; d <= HURDLE_POI_LANE_PADDING; d++) {
    if (d === 0) continue;
    const adj = getFieldAt(lanes, field.lane + d, field.pos);
    if (adj) addFieldToSet(lanes, adj, seen, out);
  }
}

/**
 * Precompute tactical envelope from board topology: 1 before, 3 behind, +/-1 lane.
 */
export function computeHurdleEnvelope(lanes: Field[][], hurdleFields: Field[]): Coordinates[] {
  const seen = new Set<string>();
  const out: Coordinates[] = [];
  const pred = buildPredecessorMap(lanes);

  for (const hf of hurdleFields) {
    addFieldToSet(lanes, hf, seen, out);
    addLateralNeighbors(lanes, hf, seen, out);

    const before = pred.get(fieldKey(hf.lane, hf.pos));
    if (before) {
      addFieldToSet(lanes, before, seen, out);
      addLateralNeighbors(lanes, before, seen, out);
    }

    let cur: Field | undefined = hf;
    for (let step = 0; step < HURDLE_POI_BEHIND_SQUARES; step++) {
      if (!cur || cur.nf < 0) break;
      const next = getFieldAt(lanes, cur.lane, cur.nf);
      if (!next) break;
      addFieldToSet(lanes, next, seen, out);
      addLateralNeighbors(lanes, next, seen, out);
      cur = next;
    }
  }

  return out;
}

/**
 * Extract hurdles from board lanes with progress metadata and precomputed envelopes.
 */
export function extractHurdlesFromLanes(lanes: Field[][]): BoardHurdle[] {
  const byProg = new Map<number, Field[]>();

  lanes.forEach((laneFields, laneIndex) => {
    laneFields.forEach((field) => {
      if (!field.hurdle || field.prog === GOAL_LINE_PROG) return;
      const list = byProg.get(field.prog) ?? [];
      field.lane = laneIndex;
      list.push(field);
      byProg.set(field.prog, list);
    });
  });

  return Array.from(byProg.entries())
    .sort(([a], [b]) => a - b)
    .map(([progress, fields]) => ({
      id: `hurdle-${progress}`,
      progress,
      fields: fields.map((f) => ({ x: f.x, y: f.y })),
      envelope: computeHurdleEnvelope(lanes, fields),
    }));
}

export interface HurdleVisibilityInput {
  round: number;
  horsesStarted: boolean[];
  horsesBehindGoal: boolean[];
}

/**
 * Same visibility rules as {@link HurdleView}: first/last hurdle gated by horse positions.
 */
export function filterVisibleHurdles(hurdles: BoardHurdle[], input: HurdleVisibilityInput): BoardHurdle[] {
  if (!Turfmaster.hurdleRounds.includes(input.round)) return [];

  const isFirstHurdleVisible = !input.horsesBehindGoal.reduce((prev, cur) => cur && prev, true);
  const isLastHurdleVisible = input.horsesStarted.reduce((prev, cur) => cur && prev, true);

  return hurdles.filter((_h, index) => {
    if (index === 0) return isFirstHurdleVisible;
    if (index === hurdles.length - 1) return isLastHurdleVisible;
    return true;
  });
}

/**
 * Fields used for hurdle SVG path (may trim end segments for first/last hurdle).
 */
export function hurdleRenderFields(
  hurdle: BoardHurdle,
  hurdleIndex: number,
  filteredHurdles: BoardHurdle[],
  visibility: HurdleVisibilityInput,
): Coordinates[] {
  const isFirstHurdleVisible = !visibility.horsesBehindGoal.reduce((prev, cur) => cur && prev, true);
  const isLastHurdleVisible = visibility.horsesStarted.reduce((prev, cur) => cur && prev, true);
  const isFirst = hurdleIndex === 0;
  const isLast = hurdleIndex === filteredHurdles.length - 1;

  if (
    (isFirstHurdleVisible && isFirst) ||
    (isLastHurdleVisible && isLast)
  ) {
    return hurdle.fields.slice(0, -2);
  }
  return [...hurdle.fields];
}

/**
 * Global next hurdle: first visible hurdle with progress > max(active horse progress).
 */
export function selectGlobalNextHurdle(
  visibleHurdles: BoardHurdle[],
  activeHorseProgress: number[],
): BoardHurdle | null {
  if (visibleHurdles.length === 0 || activeHorseProgress.length === 0) return null;

  const frontProgress = Math.max(...activeHorseProgress);
  return visibleHurdles.find((h) => h.progress > frontProgress) ?? null;
}
